import { memo, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, TableCell } from '@mui/material';
import styles from './COGSList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { HeadCelType, SortDirection } from '~/utils/types/common';

import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { formatCurrency } from '~/utils/helper';

import { MenuVerticalIcon } from '~/assets/icons';
import MenuOption from '../menu-option/MenuOption';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { ICogs, ICogsList } from '~/utils/interface/cogs';
import { cogsActions } from '~/thunks/cogs/cogsSlice';

const cx = classNames.bind(styles);

type COGSListProps = {
  onOpenEdit?: (id: string) => void;
  editData?: ICogs;
  isHideMenu?: boolean;
};

function COGSList(props: COGSListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice']);
  const dispatch = useAppDispatch();

  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { cogs, filterCogs: filter, currentPage, totalPages } = useAppSelector((state) => state.cogs);
  //#endregion Selector

  //#region Declare State
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'expenseDate',
        label: t('property.expenseDate'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'transactionType',
        label: t('property.transactionType'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'number',
        label: t('property.number'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'name',
        label: t('property.nameCogs'),
        sortable: true,
        searchable: true,
      },

      {
        id: 'split',
        label: t('property.split'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'amount',
        label: t('property.amount'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'balance',
        label: t('property.balance'),
        sortable: true,
        searchable: true,
      },
    ],
    [t]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);

  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(cogsActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleChangePage = (newPage: number) => {
    dispatch(
      cogsActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      cogsActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, selectedId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(selectedId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined && props.onOpenEdit) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(cogsActions.changeMenuSelectId(menuSelectedId));
      dispatch(cogsActions.changeOpenAlertDelete(true));
    }
  };

  //#endregion Handle Function

  return (
    <Box className={cx('invoice-list-container')}>
      <BaseTable<ICogsList>
        data={cogs}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(item: ICogsList) => {
          return (
            <>
              <TableCell>
                <Box>{dayjs(item.expenseDate).format('MM/DD/YYYY')}</Box>
              </TableCell>

              <TableCell>{item.transactionType}</TableCell>
              <TableCell>{item.number}</TableCell>
              <TableCell>{item.name}</TableCell>

              <TableCell>{item.split}</TableCell>
              <TableCell>{formatCurrency(item.amount)}</TableCell>
              <TableCell>{formatCurrency(item.balance)}</TableCell>

              <TableCell align={'left'} padding={'none'}>
                {!props.isHideMenu && (permissions[PERMISSIONS.cogs_edit] || permissions[PERMISSIONS.cogs_delete]) && (
                  <Box className='flex flex-1 justify-center'>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, item?.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                )}
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.cogs_edit]}
        enableDelete={permissions[PERMISSIONS.cogs_delete]}
      />
    </Box>
  );
}

export default memo(COGSList);
